import { EnumErrorLevel } from '../../../../../../api/com/MetaPattern/Core/Infrastructure/Common/EnumErrorLevel';
import { ValidationResult } from '../../../../../../api/com/MetaPattern/Core/Infrastructure/Common/ValidationResult';
import { EnumDisplayLanguage } from '../../../../../../api/com/MetaPattern/MDA/Designer/Base/EnumDisplayLanguage';
import { LineShape } from '../../../../../../api/com/MetaPattern/MDA/Designer/Shape/LineShape';
import { BaseElement } from '../../../../../../api/com/MetaPattern/MDA/MD/Base/BaseElement';
import * as joint from 'jointjs';
import _ from 'underscore';
import { EventBus } from '../../../Product/IDE/Util/EventBus';
import { ValidationResultHelper } from '../Base/ValidationResultHelper';
import { Diagram } from '../Repository/Diagram';
import { BaseLink } from './BaseLink';
import { MDShape } from './MDShape';
// 关系线基类，连接两个元素
export abstract class RelationshipLink extends BaseLink implements MDShape {
  private _model: BaseElement;
  private _shape: LineShape;
  private _isSelected: boolean;
  private _displayLanguage: EnumDisplayLanguage; // 显示语言
  private _isLock: boolean; // 是否锁定

  constructor(opt?: any) {
    // 调用超类的构造器
    super(opt);
    this.set('type', 'mst.RelationshipLink');
    this.connector('jumpover', { size: 5, type: 'arc', radius: 5 }); // 相交样式
    this.router('orthogonal'); // 路由：正交
    this.on('change:target', _.debounce(this.linkChangeTarget, 300));
  }

  element(): BaseElement {
    return this._model;
  }

  protected setModel(model: BaseElement): void {
    this._model = model;
    this.set('model', this._model);
    this.fromMD();
  }

  public shape(): LineShape {
    return this._shape;
  }

  setMD (lineShape: LineShape, model: BaseElement, isFromJSON: boolean, source?: any, target?: any) {
    this._shape = lineShape;
    this._model = model;
    this.set('model', this._model);
    this.set('shape', this._shape);
    if (!isFromJSON) {
      this.fromMD();
    }
    if (source || target) {
      this.off('change:target');
      if (source) this.source(source);
      if (target) this.target(target);
      this.on('change:target', _.debounce(this.linkChangeTarget, 300));
    }
  }

  protected get displayLanguage() {
    return this._displayLanguage;
  }

  protected abstract changeTarget(source: joint.dia.Element, target: joint.dia.Element): BaseElement;

  /**
   * 检查源和目标类型对当前关系是否有效
   * @param sourceType 源类型
   * @param targetType 目标类型
   * @returns 有效返回true
   */
  public abstract checkShapeType(sourceType: string, targetType: string): boolean;

  /** 图形的前端类型 */
  abstract shapeType(): string;

  private linkChangeTarget() {
    const source = this.getSourceElement();
    const target = this.getTargetElement();
    if (target !== null && source !== null) {
      const element = this.changeTarget(source, target);
      if (element !== null) {
        this.setModel(element);
        EventBus.$emit('addElement', element);
      }
    }
  }

  /** 当前图形是否被选中 */
  isSelect(): boolean {
    return this._isSelected;
  }

  select(): string {
    this.attr({ body: { stroke: '#ffffff' } });
    this._isSelected = true;
    if (this._model) {
      return this._model.designId;
    }
    return '';
  }

  unSelect() {
    this.attr({ body: { stroke: '#545c64' } });
    this._isSelected = false;
    if (this._model) {
      return this._model.designId;
    }
    return '';
  }

  /** 是否锁定，锁定的元素，不能进行修改操作 */
  isLock(): boolean {
    return this._isLock;
  }

  /** 锁定图形 */
  lock(): void {
    this._isLock = true;
  }

  /** 解锁图形 */
  unLock(): void {
    this._isLock = false;
  }

  toMD(): void {
    if (this._shape) {
      let isModified = false;
      if (this._shape.vertices !== this.vertices().toString()) {
        this._shape.vertices = JSON.stringify(this.vertices());
        isModified = true;
      }
      if (isModified) {
        this._shape.modify();
      }
    }
  }

  fromMD(): void {
    if (this._shape) {
      if (this._shape.vertices !== null && this._shape.vertices !== '') {
        this.vertices(JSON.parse(this._shape.vertices));
      }
    }
  }

  /**
   * 克隆图形，包括图形内部所有元素，不包括与其他图形的关系
   * @param diagram 克隆后所属的图表
   * @param elements 克隆的所有元数据都插入数组返回
   */
  public cloneShape(diagram: Diagram, elements: Array<BaseElement>): MDShape {
    return null;
  }

  validate(): Array<ValidationResult> {
    const source = this.getSourceElement();
    const target = this.getTargetElement();
    const errors = new Array<ValidationResult>();
    if (source === null) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, this._model.designId, this._model.designId, `关系 [${this._model.name}] 的源必须是模型元素`));
    }
    if (target === null) {
      errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, this._model.designId, this._model.designId, `关系 [${this._model.name}] 的目标必须是模型元素`));
    }
    return errors;
  }

  /**
   * 设置显示语言
   * @param displayLanguage 显示语言
   */
  setDisplayLanguage (displayLanguage: EnumDisplayLanguage) {
    this._displayLanguage = displayLanguage;
  }

  refresh(): void {
    // do nothing
  }
}
